import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { IMessageBubble } from "../../../models/interactions/MessageBubble";
import MessageBubbleAnimationDropdown from "./MessageBubbleAnimationDropdown";
import MessageBubbleActorDropdown from "./MessageBubbleActorDropdown";
import MessageBubblePreviewText from "./MessageBubblePreviewText";
import ActorPropDropdown from "../lookat/ActorPropDropdown";
import usePropHelper from "../../../hooks/PropHelper";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import { useTranslation } from "react-i18next";
import PiChipSelect, { PiChipSelectOption } from "../../../components/PiChipSelect";
import { Prop } from "../../../models/Prop";
import {
  messageBubbleStyles,
  noMessageBubbleAnimation,
} from "../../../models/interactions/MessageBubble";
import PiActorChipSelect from "../components/PiActorChipSelect";
import PiLookModeChipSelect from "../components/PiLookModeChipSelect";
import PiActorPropChipSelect from "../components/PiActorPropChipSelect";
import { PiSettingsGap, PiSettingsList, PiSettingsRow } from "../components/PiSettingsList";
import PiAnimationPicker from "../animation/PiAnimationPicker";
import { hasLookatTarget } from "../../../models/NoLookat";

type MessageBubblePreviewBodyVariant = "standalone" | "embedded";

export default function MessageBubblePreviewBody(props: {
  messageBubble: IMessageBubble;
  updateInteraction: (field: string, value: any, patch?: Partial<IMessageBubble>) => void;
  variant?: MessageBubblePreviewBodyVariant;
}) {
  const { messageBubble, updateInteraction, variant = "embedded" } = props;
  const take = useSelector(selectSelectedTake3D);
  const { getBubbleTargetProps } = usePropHelper();
  const propsInTake = take
    ? getBubbleTargetProps(take, {
        kind: "timeline",
        interactionId: messageBubble.id,
      })
    : [];
  const isPropIdInTake = (objectId: string) =>
    propsInTake.some((prop) => prop.id === objectId);
  const isPropAnchor = isPropIdInTake(messageBubble.npcId);
  const isSelfLookAt =
    !!messageBubble.lookAt && messageBubble.lookAt === messageBubble.npcId;
  const handleAnchorChange = (objectId: string) => {
    // A prop can't look at anything or play an animation.
    if (isPropIdInTake(objectId)) {
      updateInteraction("npcId", objectId, {
        lookAt: "",
        animation: { ...noMessageBubbleAnimation },
      });
      return;
    }
    // An actor can't look at itself.
    if (objectId === messageBubble.lookAt) {
      updateInteraction("npcId", objectId, { lookAt: "" });
      return;
    }

    updateInteraction("npcId", objectId);
  };

  useEffect(() => {
    if (!messageBubble.lookAt) return;
    if (!isPropAnchor && !isSelfLookAt) return;
    updateInteraction("lookAt", "");
  }, [isPropAnchor, isSelfLookAt, messageBubble.lookAt, updateInteraction]);

  if (variant === "embedded") {
    return (
      <div className="w-full h-full flex flex-col gap-5">
        <div className="w-full grid grid-cols-1 piLg:grid-cols-[minmax(260px,34%)_minmax(320px,max-content)] gap-7 items-start">
          <div className="flex flex-col gap-5 min-w-0">
            <ActorPropDropdown
              label={"Actor"}
              selectedObjectId={messageBubble.npcId}
              onClick={handleAnchorChange}
              availableProps={propsInTake}
            />
            {isPropAnchor ? (
              <DisabledLookAtField label="Look at" />
            ) : (
              <MessageBubbleActorDropdown
                label={"Look at"}
                selectedTargetActorId={messageBubble.lookAt}
                onClick={(actorId) => updateInteraction("lookAt", actorId)}
                excludeActors={[messageBubble.npcId]}
                allowNoActor
              />
            )}
          </div>
          <div className="min-w-0">
            <SettingsSection label="Animation">
              <MessageBubbleAnimationDropdown
                selectedAnimation={messageBubble.animation}
                onClick={(anim) => updateInteraction("animation", anim)}
                variant="preview"
              />
            </SettingsSection>
          </div>
        </div>
        <div className="w-full flex-1 min-h-0">
          <MessageBubblePreviewText
            messageBubble={messageBubble}
            updateInteraction={updateInteraction}
            variant="embedded"
          />
        </div>
      </div>
    );
  }

  // Design: Design Studio `screens/lookat-rework/r2-bubble.html`. The Look (head only /
  // whole body) and Timing rows are left out until the game side supports them.
  return (
    <StandaloneMessageBubbleBody
      messageBubble={messageBubble}
      updateInteraction={updateInteraction}
      propsInTake={propsInTake}
      isPropAnchor={isPropAnchor}
      onAnchorChange={handleAnchorChange}
    />
  );
}

function StandaloneMessageBubbleBody(props: {
  messageBubble: IMessageBubble;
  updateInteraction: (field: string, value: any, patch?: Partial<IMessageBubble>) => void;
  propsInTake: Prop[];
  isPropAnchor: boolean;
  onAnchorChange: (objectId: string) => void;
}) {
  const { messageBubble, updateInteraction, propsInTake, isPropAnchor } = props;
  const { t } = useTranslation();

  const styleOptions: PiChipSelectOption[] = messageBubbleStyles.map((style) => ({
    id: style,
    label: style,
    icon: <ChatBubbleOutlineOutlinedIcon className="flex-none" sx={{ fontSize: 18 }} />,
  }));

  return (
    <div className="w-full h-full min-h-0 grid grid-cols-[minmax(0,1fr)_380px] gap-4">
      <section className="min-h-0 flex items-center justify-center p-4 rounded-lg border border-solid border-interaction-primary-washed bg-interaction-primary-washed overflow-hidden">
        <div className="w-full max-w-[620px]">
          <MessageBubblePreviewText
            messageBubble={messageBubble}
            updateInteraction={updateInteraction}
            variant="standalone"
            hideStyleDropdown
          />
        </div>
      </section>

      <PiSettingsList className="min-h-0 max-h-full overflow-y-auto pit-scrollbar">
        <PiSettingsRow label={String(t("bubble_ui.speaker"))}>
          <PiActorPropChipSelect
            aria-label={String(t("bubble_ui.speaker"))}
            value={messageBubble.npcId}
            onChange={props.onAnchorChange}
            availableProps={propsInTake}
          />
        </PiSettingsRow>
        <PiSettingsRow label={String(t("bubble_ui.talks_to"))} disabled={isPropAnchor}>
          <PiActorChipSelect
            aria-label={String(t("bubble_ui.talks_to"))}
            value={isPropAnchor ? "" : messageBubble.lookAt}
            onChange={(actorId) => updateInteraction("lookAt", actorId)}
            excludeActors={[messageBubble.npcId]}
            allowKeepCurrent
            allowNoActor
            // A prop cannot look at anyone.
            disabled={isPropAnchor}
          />
        </PiSettingsRow>
        {/* How the speaker looks at the listener; only meaningful when there is one. */}
        <PiSettingsRow
          label={String(t("look_mode.label"))}
          disabled={isPropAnchor || !hasLookatTarget(messageBubble.lookAt)}
        >
          <PiLookModeChipSelect
            value={messageBubble.finalIK}
            onChange={(finalIK) => updateInteraction("finalIK", finalIK)}
            disabled={isPropAnchor || !hasLookatTarget(messageBubble.lookAt)}
          />
        </PiSettingsRow>

        <PiSettingsGap />
        <PiSettingsRow
          label={String(t("bubble_ui.animation"))}
          below={
            <PiAnimationPicker
              disabled={isPropAnchor}
              selectedAnimation={messageBubble.animation}
              onChange={(animation) => updateInteraction("animation", animation)}
            />
          }
        />
        <PiSettingsRow label={String(t("bubble_ui.bubble_style"))}>
          <PiChipSelect
            aria-label={String(t("bubble_ui.bubble_style"))}
            tone="setting"
            value={messageBubble.messageBubbleStyle}
            options={styleOptions}
            onChange={(style) => updateInteraction("messageBubbleStyle", style)}
            placeholder={messageBubbleStyles[0]}
          />
        </PiSettingsRow>
      </PiSettingsList>
    </div>
  );
}

function DisabledLookAtField(props: { label: string }) {
  return (
    <div className="w-full h-[98px] opacity-60">
      {props.label && (
        <div className="text-base font-bold text-interaction-primary h-[30px]">
          {props.label}
        </div>
      )}
      <div className="flex p-4 w-full h-[68px] align-middle border-b-2 border-solid bg-interaction-softBackground border-interaction-primary items-center cursor-not-allowed">
        <div className="w-full font-medium text-interaction-primary text-interaction text-interaction-base">
          No actor
        </div>
      </div>
    </div>
  );
}

function SettingsSection(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 min-w-0 min-h-0">
      {props.label && (
        <div className="text-base font-bold text-interaction-primary">
          {props.label}
        </div>
      )}
      {props.children}
    </div>
  );
}
